/**
 * Карта прокси для расширения: какие двери открывать.
 *
 * Секрет едет отдельным каналом в SW. Та же строка лежит в счёте и в профиле.
 */

import { countryLabel } from '../next/roads';

export const ROADS = 'forq:roads';
export const ROADS_SECRET = 'forq:roads:secret';
export const ROADS_PROBE = 'forq:roads:probe';
export const ROADS_OK = 'forq:roads:ok';

export interface RoadsMap {
  doors: Record<string, string>;
  books: string[];
  deskOn: boolean;
}

export function roadsEnvelope(map: RoadsMap): { channel: string } & RoadsMap {
  return { channel: ROADS, ...map };
}

export function parseRoads(data: unknown): RoadsMap | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== ROADS) return null;
  if (typeof body.doors !== 'object' || body.doors === null) return null;
  if (!Array.isArray(body.books)) return null;
  const doors: Record<string, string> = {};
  for (const [venue, raw] of Object.entries(body.doors)) {
    if (typeof raw === 'string' && raw !== '') doors[venue] = raw;
  }
  const books = body.books.filter((one): one is string => typeof one === 'string' && one !== '');
  return { doors, books, deskOn: body.deskOn === true };
}

export function secretEnvelope(
  venue: string,
  line: string,
): { channel: string; venue: string; line: string } {
  return { channel: ROADS_SECRET, venue, line };
}

export function parseSecret(data: unknown): { venue: string; line: string } | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== ROADS_SECRET) return null;
  if (typeof body.venue !== 'string' || body.venue === '') return null;
  if (typeof body.line !== 'string') return null;
  return { venue: body.venue, line: body.line };
}

export function probeEnvelope(
  venue: string,
  nonce = 0,
): { channel: string; venue: string; nonce: number } {
  return { channel: ROADS_PROBE, venue, nonce };
}

export function parseProbe(data: unknown): { venue: string; nonce: number } | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== ROADS_PROBE) return null;
  if (typeof body.venue !== 'string' || body.venue === '') return null;
  const nonce = typeof body.nonce === 'number' && Number.isFinite(body.nonce) ? body.nonce : 0;
  return { venue: body.venue, nonce };
}

export interface ProbeRow {
  venue: string;
  name: string;
  host: string;
  via: string;
  ms: number | null;
  country: string;
  ip?: string;
  fail: string;
}

export type RoadsPhase = 'apply' | 'probe';

export interface RoadsOk {
  live: boolean;
  say: string;
  control: string;
  webrtc: string;
  have: Record<string, boolean>;
  rows: ProbeRow[];
  /** apply — PAC поставили, это не отчёт пробы. probe — один ответ на «проверить». */
  phase?: RoadsPhase;
  nonce?: number;
}

export function okEnvelope(one: RoadsOk): { channel: string } & RoadsOk {
  return { channel: ROADS_OK, ...one };
}

export function parseOk(data: unknown): RoadsOk | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as Record<string, unknown>;
  if (body.channel !== ROADS_OK) return null;
  if (typeof body.live !== 'boolean') return null;
  if (typeof body.say !== 'string') return null;
  const haveRaw = body.have;
  const have: Record<string, boolean> = {};
  if (typeof haveRaw === 'object' && haveRaw !== null) {
    for (const [venue, raw] of Object.entries(haveRaw as Record<string, unknown>)) {
      have[venue] = raw === true;
    }
  }
  const rows = Array.isArray(body.rows)
    ? body.rows.filter((one): one is ProbeRow => {
        if (typeof one !== 'object' || one === null) return false;
        const row = one as ProbeRow;
        return typeof row.venue === 'string' && typeof row.via === 'string' && typeof row.fail === 'string';
      })
    : [];
  return {
    live: body.live,
    say: body.say,
    control: typeof body.control === 'string' ? body.control : '',
    webrtc: typeof body.webrtc === 'string' ? body.webrtc : '',
    have,
    rows,
    phase: body.phase === 'apply' || body.phase === 'probe' ? body.phase : undefined,
    nonce: typeof body.nonce === 'number' && Number.isFinite(body.nonce) ? body.nonce : undefined,
  };
}

/** Один отчёт: открылось · страна · адрес · время. Не мелькающие куски. */
export function reportOf(row: ProbeRow): string {
  if (row.fail !== '') return row.fail;
  const parts = ['открылось'];
  const where = countryLabel(row.country);
  if (where !== '') parts.push(where);
  if (row.ip) parts.push(row.ip);
  if (row.ms !== null) {
    parts.push(row.ms < 1000 ? `${row.ms} мс` : `${(Math.round(row.ms / 100) / 10).toFixed(1)} с`);
  }
  return parts.join(' · ');
}

/** Строка под полем. apply без пробы — молчим, иначе старый отказ перебивает ожидание. */
export function sayOf(ok: RoadsOk, venue: string): string {
  if (ok.phase === 'apply') return ok.say;
  const row = ok.rows.find((one) => one.venue === venue);
  if (row !== undefined) return reportOf(row);
  return ok.say;
}
